import { dwColor } from '~/theme/tokens';
import { SILK_FRAGMENT_SHADER, SILK_VERTEX_SHADER } from './silkShader';

export interface SilkRenderer {
  setRunning: (running: boolean) => void;
  resize: () => void;
  destroy: () => void;
}

/** Ruch jest wolny — 30 klatek wystarcza i połowi koszt na zintegrowanej grafice. */
const FRAME_MS = 1000 / 30;
/** Tyle kolejnych spóźnionych klatek obniża rozdzielczość płótna o jeden krok. */
const SLOW_FRAMES_LIMIT = 20;
const MIN_QUALITY = 0.5;
/** Poniżej tej szerokości (telefon) wachlarz startuje od lewej krawędzi, z dala od logo na środku. */
const NARROW_WIDTH = 560;
/** Czas startowy: pierwsza klatka nie zaczyna od „zera”, w którym wstęgi są najbardziej symetryczne. */
const START_TIME = 6;

const SILK_COLORS = {
  uSky: dwColor.seq2,
  uSignal: dwColor.signal,
  uAction: dwColor.action,
  uBrand: dwColor.brand,
  uAmber: dwColor.authAmber,
} as const;

export function hexToRgb(hex: string): [number, number, number] {
  return [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16) / 255) as [
    number,
    number,
    number,
  ];
}

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
}

/**
 * Rysuje wstęgi na płótnie. Zwraca `null`, gdy WebGL jest niedostępny albo shader się nie skompilował —
 * wtedy tło niesie wariant CSS. `onContextLost` informuje, że trzeba wrócić do wariantu CSS.
 */
export function createSilkRenderer(
  canvas: HTMLCanvasElement,
  onContextLost: () => void
): SilkRenderer | null {
  const gl = canvas.getContext('webgl', {
    alpha: true,
    antialias: false,
    premultipliedAlpha: true,
    preserveDrawingBuffer: false,
    powerPreference: 'low-power',
  });
  if (!gl) return null;

  const vertex = compile(gl, gl.VERTEX_SHADER, SILK_VERTEX_SHADER);
  const fragment = compile(gl, gl.FRAGMENT_SHADER, SILK_FRAGMENT_SHADER);
  const program = gl.createProgram();
  if (!vertex || !fragment || !program) return null;
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);

  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, 'p');
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
  Object.entries(SILK_COLORS).forEach(([uniform, hex]) => {
    gl.uniform3fv(gl.getUniformLocation(program, uniform), hexToRgb(hex));
  });
  const uResolution = gl.getUniformLocation(program, 'uResolution');
  const uTime = gl.getUniformLocation(program, 'uTime');
  const uStart = gl.getUniformLocation(program, 'uStart');

  let frame = 0;
  let running = false;
  let lost = false;
  let startedAt = performance.now();
  let elapsed = START_TIME;
  let lastFrameAt = 0;
  let slowFrames = 0;
  let quality = 1;

  const draw = (now: number) => {
    gl.uniform1f(uTime, elapsed + (running ? (now - startedAt) / 1000 : 0));
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  const resize = () => {
    if (lost) return;
    // 1 piksel płótna na piksel CSS (bez mnożenia przez DPR): włókna zostają ostre, koszt ~2,25× niższy.
    const scale = Math.min(window.devicePixelRatio || 1, 1) * quality;
    const width = Math.max(1, Math.round(canvas.clientWidth * scale));
    const height = Math.max(1, Math.round(canvas.clientHeight * scale));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
      gl.uniform2f(uResolution, width, height);
    }
    gl.uniform1f(uStart, canvas.clientWidth < NARROW_WIDTH ? 0.04 : 0.3);
    draw(performance.now());
  };

  const loop = (now: number) => {
    frame = requestAnimationFrame(loop);
    const delta = now - lastFrameAt;
    if (delta < FRAME_MS - 2) return;
    // Słaby GPU: klatki stale się spóźniają → niższa rozdzielczość płótna (do połowy).
    slowFrames = lastFrameAt && delta > FRAME_MS * 1.5 ? slowFrames + 1 : 0;
    lastFrameAt = now;
    if (slowFrames > SLOW_FRAMES_LIMIT && quality > MIN_QUALITY) {
      quality = Math.max(MIN_QUALITY, quality * 0.75);
      slowFrames = 0;
      resize();
    }
    draw(now);
  };

  const setRunning = (next: boolean) => {
    if (next === running || lost) return;
    const now = performance.now();
    if (next) {
      startedAt = now;
      lastFrameAt = 0;
      running = true;
      frame = requestAnimationFrame(loop);
    } else {
      elapsed += (now - startedAt) / 1000;
      running = false;
      cancelAnimationFrame(frame);
      draw(now);
    }
  };

  const handleContextLost = (event: Event) => {
    event.preventDefault();
    lost = true;
    running = false;
    cancelAnimationFrame(frame);
    onContextLost();
  };
  canvas.addEventListener('webglcontextlost', handleContextLost);

  resize();

  return {
    setRunning,
    resize,
    destroy: () => {
      cancelAnimationFrame(frame);
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      // Zwolnienie kontekstu od razu (wyloguj/zaloguj w jednej sesji tworzy nowe płótno).
      if (!lost) gl.getExtension('WEBGL_lose_context')?.loseContext();
    },
  };
}
